import { Layer, Stage } from "react-konva";
import RectangleElement from "../rectangle/RectangleElement";
import {
  calculateGroupHeight,
  calculateTextBlockWidth,
  estimateKonvaTextHeight,
  formatListContent,
  getTextStyle,
  hasNoteContent,
  hasTaskContent,
  hasUserContent,
  stripHtmlPreserveFormatting,
} from "../../../SectionElements/plugins/utils";
import TextElement from "../text/TextElement";
import CircleElement from "../circle/CircleElement";
import ArrowElement from "../arrow/ArrowElement";
import LineElement from "../line/LineElement";
import DoubleSidedArrowElement from "../arrow/DoubleSidedArrowElement";
import ImageElement from "../image/ImageElement";
import CanvasImage from "./CanvasImage";
import { useState, useEffect } from "react";

// Component to handle image loading
const LoadedImageElement = ({ imageInfo }) => {
  const [loadedImage, setLoadedImage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const image = new Image();
    // Set crossOrigin only for http/https URLs to avoid issues with data/blob URLs
    try {
      if (/^https?:/i.test(imageInfo.src)) {
        image.crossOrigin = "anonymous";
      }
    } catch {}

    image.onload = () => {
      setLoadedImage(image);
      setIsLoading(false);
    };

    image.onerror = (error) => {
      console.error("Error loading image:", error);
      setIsLoading(false);
    };

    image.src = imageInfo.src;
  }, [imageInfo.src]);

  if (isLoading || !loadedImage) {
    return null; // Don't render until image is loaded
  }

  // Create the final image info with the loaded image
  const finalImageInfo = {
    ...imageInfo,
    image: loadedImage,
  };

  return <ImageElement isDraggable={false} img={finalImageInfo} />;
};

const CanvasDisplay = ({ pages }) => {
  // Debug: Log page data to understand ordering

  // Sort pages by pageOrder first, then fallback to created_at timestamp
  const sortedPages = pages
    .filter((p) => p && (p.canvas_page_id || p.page_id)) // support both old and new ID formats
    .sort((a, b) => {
      // First try to sort by pageOrder if available
      if (a.pageOrder !== undefined && b.pageOrder !== undefined) {
        return a.pageOrder - b.pageOrder;
      }
      // Fallback to created_at timestamp (oldest first)
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateA - dateB; // ascending order (oldest first)
    });


  return (
    <>
      {sortedPages.map((page, pageIndex) => (
        <div key={pageIndex} style={{ marginBottom: "20px" }}>
          <div
            style={{
              width: "50vw",
              // Prefer explicit height from layout_config if provided, else default 120vh
              height: `${page?.layout_config?.layout?.page_height_vh || 120}vh`,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
              overflow: "hidden",
              margin: "0 auto",
            }}
          >
            {/* If canva_snap_url exists, show the snapshot image instead of rendering elements */}
            {page.canva_snap_url ? (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <img
                  src={page.canva_snap_url}
                  alt={`Canvas Snapshot ${pageIndex + 1}`}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
              </div>
            ) : (
              /* Fallback to rendering individual elements if no snapshot URL */
              <Stage
                width={window.innerWidth * 0.45}
                height={
                  window.innerHeight *
                  ((page?.layout_config?.layout?.page_height_vh || 120) / 100)
                }
              >
                {/* Images Layer - Renders first (bottom of stack) */}
                <Layer>
                  {/* Handle new image_data structure first */}
                  {page.image_data?.map((image, i) => {
                    const props = image.image_properties || {};
                    const imageInfo = {
                      id: props.id || i,
                      src: image.image_path || "",
                      x: props.x || 0,
                      y: props.y || 0,
                      width: props.width || props.originalWidth || 100,
                      height: props.height || props.originalHeight || 100,
                      rotation: props.rotation || 0,
                      scaleX: props.scaleX || 1,
                      scaleY: props.scaleY || 1,
                      cropData: props.cropData,
                      originalCropRotation: props.originalCropRotation ?? 0,
                      hasBakedTransforms: props.hasBakedTransforms ?? false,
                      originalWidth: props.originalWidth,
                      originalHeight: props.originalHeight,
                      flipHorizontal: props.flipHorizontal || false,
                      flipVertical: props.flipVertical || false,
                    };

                    return (
                      <LoadedImageElement
                        key={props.id || i}
                        imageInfo={imageInfo}
                      />
                    );
                  })}

                  {/* Fallback to old images structure for backward compatibility */}
                  {!page.image_data &&
                    page.images?.map((image, i) => {
                      const props = image.image_properties || {};
                      const imageInfo = {
                        id: image.image_id || props.id || i,
                        src: image.image_path || "",
                        x: props.x || 0,
                        y: props.y || 0,
                        width: props.width || props.originalWidth || 100,
                        height: props.height || props.originalHeight || 100,
                        rotation: props.rotation || 0,
                        scaleX: props.scaleX || 1,
                        scaleY: props.scaleY || 1,
                        cropData: props.cropData,
                        originalCropRotation: props.originalCropRotation ?? 0,
                        hasBakedTransforms: props.hasBakedTransforms ?? false,
                        originalWidth: props.originalWidth,
                        originalHeight: props.originalHeight,
                        flipHorizontal: props.flipHorizontal || false,
                        flipVertical: props.flipVertical || false,
                      };

                      return (
                        <LoadedImageElement
                          key={image.image_id || props.id || i}
                          imageInfo={imageInfo}
                        />
                      );
                    })}

                  {/* Handle image shapes from shape_data only when no image_data/images (avoid duplicate images) */}
                  {!page.image_data?.length && !page.images?.length && page.shape_data?.map((shape, i) => {
                    if (shape.type === "image") {
                      const props = shape.shape_properties || shape.shape_info || {};
                      const imageInfo = {
                        id: props.id || i,
                        src: props.src || props.image_path || "",
                        x: props.x || 0,
                        y: props.y || 0,
                        width: props.width || props.originalWidth || 100,
                        height: props.height || props.originalHeight || 100,
                        rotation: props.rotation || 0,
                        scaleX: props.scaleX || 1,
                        scaleY: props.scaleY || 1,
                        cropData: props.cropData,
                        originalCropRotation: props.originalCropRotation ?? 0,
                        hasBakedTransforms: props.hasBakedTransforms ?? false,
                        originalWidth: props.originalWidth,
                        originalHeight: props.originalHeight,
                        flipHorizontal: props.flipHorizontal || false,
                        flipVertical: props.flipVertical || false,
                      };

                      return (
                        <LoadedImageElement
                          key={props.id || i}
                          imageInfo={imageInfo}
                        />
                      );
                    }
                    return null;
                  })}
                </Layer>

                {/* Other Elements Layer - Renders second (top of stack) */}
                <Layer>
                  {/* Handle new shape_data structure first */}
                  {page.shape_data?.map((shape, i) => {
                    // Skip images as they're handled in the images layer
                    if (shape.type === "image") return null;

                    // Get shape data from the new structure
                    const getShapeData = (shape) => {
                      // Try to get data from shape_info first, then shape_properties
                      const shapeInfo = shape.shape_info || {};
                      const shapeProperties = shape.shape_properties || {};

                      // Combine both sources, with shape_properties taking precedence
                      return {
                        ...shapeInfo,
                        ...shapeProperties,
                        type: shape.type,
                        id: shapeInfo.id || shapeProperties.id || i,
                      };
                    };

                    const info = getShapeData(shape);

                    // Render rectangles
                    if (shape.type === "rectangle") {
                      return (
                        <RectangleElement
                          key={info.id || i}
                          rectangle={{
                            ...info,
                            x: info.x || 0,
                            y: info.y || 0,
                            width: info.width || 100,
                            height: info.height || 100,
                            fill: info.fill || "#ffffff",
                            stroke:
                              info.stroke || info.strokeColor || "#000000",
                            strokeSize: info.strokeWidth || 2,
                          }}
                          isDraggable={false}
                          strokeColor={
                            info.stroke || info.strokeColor || "#000000"
                          }
                          onClick={() => {}}
                          onDragEnd={() => {}}
                          onTransformEnd={() => {}}
                        />
                      );
                    }

                    // Render text
                    if (shape.type === "text") {
                      const textInfo = {
                        ...info,
                        showBackground: info.showBackground || false,
                        content: info.content || "",
                        plainText: info.plainText || "",
                        isHtml: info.isHtml || false,
                        partsByType: info.partsByType || {},
                        x: info.x || 0,
                        y: info.y || 0,
                        width: info.width || 225,
                        height: info.height || 100,
                      };

                      return (
                        <TextElement
                          text={textInfo}
                          hasUserContent={hasUserContent}
                          setSelectedObject={() => {}}
                          setShowTextBox={() => {}}
                          setSelectedImage={() => {}}
                          isDraggable={false}
                          getTextStyle={getTextStyle}
                          stripHtmlPreserveFormatting={
                            stripHtmlPreserveFormatting
                          }
                          estimateKonvaTextHeight={estimateKonvaTextHeight}
                          formatListContent={formatListContent}
                          hasNoteContent={hasNoteContent}
                          hasTaskContent={hasTaskContent}
                          textGroupHeight={calculateGroupHeight(textInfo)}
                          textBlockWidth={calculateTextBlockWidth(textInfo)}
                          canvasWidth={window.innerWidth * 0.45}
                          canvasHeight={window.innerHeight * 1.2}
                        />
                      );
                    }

                    // Render circles
                    if (shape.type === "circle") {
                      return (
                        <CircleElement
                          key={info.id || i}
                          circle={{
                            ...info,
                            x: info.x || 0,
                            y: info.y || 0,
                            radius: info.radius || 50,
                            fill: info.fill || info.fillColor || "#ffffff",
                            stroke:
                              info.stroke || info.strokeColor || "#000000",
                            strokeSize: info.strokeWidth || 2,
                          }}
                          isDraggable={false}
                          strokeColor={
                            info.stroke || info.strokeColor || "#000000"
                          }
                          onClick={() => {}}
                          onDragEnd={() => {}}
                          onTransformEnd={() => {}}
                        />
                      );
                    }

                    // Render arrows
                    if (shape.type === "arrow") {
                      const arrowX = info.x || 0;
                      const arrowY = info.y || 0;
                      let cleanedPoints = [10, 10, 100, 100];

                      if (
                        Array.isArray(info.points) &&
                        info.points.length >= 4
                      ) {
                        cleanedPoints = info.points.map((val, idx) =>
                          idx % 2 === 0 ? val - arrowX : val - arrowY
                        );
                      }

                      return (
                        <ArrowElement
                          key={info.id || i}
                          arrow={{
                            ...info,
                            id: info.id,
                            x: arrowX,
                            y: arrowY,
                            points: cleanedPoints,
                          }}
                          hideBlueCircles={true}
                          isDraggable={false}
                        />
                      );
                    }

                    // Render lines (convert absolute points to relative using x,y like arrows)
                    if (shape.type === "lines") {
                      const lineX = info.x || 0;
                      const lineY = info.y || 0;
                      const cleanedPoints =
                        Array.isArray(info.points) && info.points.length >= 4
                          ? info.points.map((val, idx) =>
                              idx % 2 === 0 ? val - lineX : val - lineY
                            )
                          : [10, 10, 100, 100];

                      return (
                        <LineElement
                          key={info.id || i}
                          hideBlueCircles={true}
                          line={{
                            ...info,
                            x: lineX,
                            y: lineY,
                            points: cleanedPoints,
                            strokeColor:
                              info.stroke ||
                              info.strokeColor ||
                              info.fillColor ||
                              "#000000",
                          }}
                          isDraggable={false}
                          strokeColor={
                            info.stroke ||
                            info.strokeColor ||
                            info.fillColor ||
                            "#000000"
                          }
                        />
                      );
                    }

                    // Render double-sided arrows (convert absolute points to relative using x,y)
                    if (shape.type === "double_sided_arrow") {
                      const arrowX = info.x || 0;
                      const arrowY = info.y || 0;
                      const cleanedPoints =
                        Array.isArray(info.points) && info.points.length >= 4
                          ? info.points.map((val, idx) =>
                              idx % 2 === 0 ? val - arrowX : val - arrowY
                            )
                          : [10, 10, 100, 100];

                      return (
                        <DoubleSidedArrowElement
                          key={info.id || i}
                          hideBlueCircles={true}
                          arrow={{
                            ...info,
                            x: arrowX,
                            y: arrowY,
                            points: cleanedPoints,
                          }}
                          strokeColor={
                            info.stroke ||
                            info.fillColor ||
                            info.strokeColor ||
                            "#000000"
                          }
                          isDraggable={false}
                        />
                      );
                    }

                    return null;
                  })}

                  {/* Fallback to old shapes structure for backward compatibility */}
                  {!page.shape_data &&
                    page.shapes?.map((shape, i) => {
                      // Handle new API structure where shape data is in layout_config
                      const getShapeData = (shape, pageIndex) => {
                        if (
                          pages[pageIndex] &&
                          pages[pageIndex].layout_config
                        ) {
                          const layout = pages[pageIndex].layout_config.layout;
                          if (layout && layout.columns) {
                            // Find the element in layout columns by matching shape type and position
                            for (const column of layout.columns) {
                              if (column.elements) {
                                // Find element by type first, then by other properties
                                const element = column.elements.find((el) => {
                                  // Match by type
                                  if (el.type !== shape.type) return false;

                                  // For circles, check radius
                                  if (shape.type === "circle" && el.radius) {
                                    return true;
                                  }

                                  // For rectangles, check size
                                  if (shape.type === "rectangle" && el.size) {
                                    return true;
                                  }

                                  // For images, check size
                                  if (shape.type === "image" && el.size) {
                                    return true;
                                  }

                                  return true;
                                });

                                if (element) {
                                  return {
                                    ...element,
                                    type: shape.type,
                                    shape_id: shape.shape_id,
                                  };
                                }
                              }
                            }
                          }
                        }
                        // Fallback to shape_properties (new API structure)
                        return shape.shape_properties || shape.shape_info || {};
                      };

                      const info = getShapeData(shape, pageIndex);

                      // Render rectangles
                      if (shape.type === "rectangle") {
                        return (
                          <RectangleElement
                            key={shape.shape_id || info.id || i}
                            rectangle={{
                              ...info,
                              ...shape.shape_properties,
                              fill: shape.shape_properties?.fill ?? "#ffffff",
                              stroke:
                                shape.shape_properties?.stroke ??
                                info.stroke ??
                                info.strokeColor ??
                                "#000000",
                              strokeSize: 2,
                            }}
                            isDraggable={false}
                            strokeColor={
                              shape.shape_properties?.stroke ??
                              info.stroke ??
                              info.strokeColor ??
                              "#000000"
                            }
                            onClick={() => {}}
                            onDragEnd={() => {}}
                            onTransformEnd={() => {}}
                          />
                        );
                      }

                      // Render text
                      if (shape.type === "text") {
                        const textInfo = {
                          ...info,
                          showBackground:
                            shape.shape_properties?.showBackground ??
                            info.showBackground ??
                            false,
                          content:
                            shape.shape_properties?.content ||
                            info.content ||
                            "",
                          plainText:
                            shape.shape_properties?.plainText ||
                            info.plainText ||
                            "",
                          isHtml:
                            shape.shape_properties?.isHtml ??
                            info.isHtml ??
                            false,
                          partsByType:
                            shape.shape_properties?.partsByType ||
                            info.partsByType ||
                            {},
                        };

                        return (
                          <TextElement
                            text={textInfo}
                            hasUserContent={hasUserContent}
                            setSelectedObject={() => {}}
                            setShowTextBox={() => {}}
                            setSelectedImage={() => {}}
                            isDraggable={false}
                            getTextStyle={getTextStyle}
                            stripHtmlPreserveFormatting={
                              stripHtmlPreserveFormatting
                            }
                            estimateKonvaTextHeight={estimateKonvaTextHeight}
                            formatListContent={formatListContent}
                            hasNoteContent={hasNoteContent}
                            hasTaskContent={hasTaskContent}
                            textGroupHeight={calculateGroupHeight(textInfo)}
                            textBlockWidth={calculateTextBlockWidth(textInfo)}
                            canvasWidth={window.innerWidth * 0.45}
                            canvasHeight={window.innerHeight * 1.2}
                          />
                        );
                      }

                      // Render circles
                      if (shape.type === "circle") {
                        return (
                          <CircleElement
                            key={shape.shape_id || shape.shape_info?.id || i}
                            circle={{
                              ...info,
                              id: shape.shape_id,
                              x: info.position?.x || 0,
                              y: info.position?.y || 0,
                              radius: info.radius || 50,
                              fill:
                                shape.shape_properties?.fill ||
                                info.style?.fillColor ||
                                "#ffffff",
                              stroke:
                                shape.shape_properties?.stroke ??
                                info.stroke ??
                                info.strokeColor ??
                                "#000000",
                              strokeSize: 2,
                              ...shape.shape_properties,
                            }}
                            isDraggable={false}
                            strokeColor={
                              shape.shape_properties?.stroke ??
                              info.stroke ??
                              info.strokeColor ??
                              "#000000"
                            }
                            onClick={() => {}}
                            onDragEnd={() => {}}
                            onTransformEnd={() => {}}
                          />
                        );
                      }

                      // Render arrows
                      if (shape.type === "arrow") {
                        const arrowX = info.x ?? info.position?.x ?? 0;
                        const arrowY = info.y ?? info.position?.y ?? 0;
                        let cleanedPoints = [10, 10, 100, 100];
                        if (
                          Array.isArray(info.points) &&
                          info.points.length >= 4
                        ) {
                          cleanedPoints = info.points.map((val, idx) =>
                            idx % 2 === 0 ? val - arrowX : val - arrowY
                          );
                        }
                        return (
                          <ArrowElement
                            key={shape.shape_id || info.id || i}
                            arrow={{
                              ...shape,
                              ...info,
                              id: shape.shape_id || info.id,
                              x: arrowX,
                              y: arrowY,
                              points: cleanedPoints,
                              ...shape.shape_properties,
                            }}
                            hideBlueCircles={true}
                            isDraggable={false}
                          />
                        );
                      }

                      if (shape.type === "lines") {
                        const cleanedPoints =
                          Array.isArray(info.points) && info.points.length >= 4
                            ? info.points.map((val) => Math.max(10, val))
                            : [10, 10, 100, 100];

                        return (
                          <LineElement
                            key={info.id || shape.shape_id || i}
                            hideBlueCircles={true}
                            line={{
                              ...shape,
                              ...info,
                              points: cleanedPoints,
                              ...shape.shape_properties,
                              strokeColor:
                                shape.shape_properties?.stroke ||
                                info.stroke ||
                                info.strokeColor ||
                                info.fillColor ||
                                "#000000",
                            }}
                            isDraggable={false}
                            strokeColor={
                              shape.shape_properties?.stroke ||
                              info.stroke ||
                              info.strokeColor ||
                              info.fillColor ||
                              "#000000"
                            }
                          />
                        );
                      }

                      if (shape.type === "double_sided_arrow") {
                        const cleanedPoints =
                          Array.isArray(info.points) && info.points.length >= 4
                            ? info.points.map((val) => Math.max(10, val))
                            : [10, 10, 100, 100];

                        return (
                          <DoubleSidedArrowElement
                            key={info.id || shape.shape_id || i}
                            hideBlueCircles={true}
                            arrow={{
                              ...shape,
                              ...info,
                              points: cleanedPoints,
                              ...shape.shape_properties,
                            }}
                            strokeColor={
                              shape.shape_properties?.stroke ||
                              info.fillColor ||
                              info.strokeColor ||
                              info.stroke ||
                              "#000000"
                            }
                            isDraggable={false}
                          />
                        );
                      }

                      return null;
                    })}
                </Layer>
              </Stage>
            )}
          </div>
        </div>
      ))}
    </>
  );
};

export default CanvasDisplay;
